import { HttpClient, type HttpDownloadProgressEvent, HttpEventType } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import {
  latestMessageRoleIs,
  responseIsInProgress,
  summaryIsInProgress,
  type IChat,
} from '@informally/common/chat';
import type { IFrontendAgentStreamEvent } from '@suvo/shared/agents';
import type { FromAPI } from '@suvo/shared/interface-utils';
import { lastValueFrom, Subscription } from 'rxjs';

const initialGeneratingText = 'Thinking about your request';
const reasoningEffortStorageKey = 'chatReasoningEffort';

export type ChatReasoningEffort = 'low' | 'medium' | 'high';

export interface IChatCapabilities {
  enabled: boolean;
  dataSources: Array<{
    alias: string;
    name: string;
    restrictions?: {
      countLimit?: number;
      maxUpdatedDaysAgo?: number;
    };
  }>;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly httpClient = inject(HttpClient);
  private responseStreamSubscription?: Subscription;

  private getInitialReasoningEffort(): ChatReasoningEffort {
    const stored = localStorage.getItem(reasoningEffortStorageKey);
    if (stored === 'low' || stored === 'medium' || stored === 'high') {
      return stored;
    }

    return 'medium';
  }

  readonly latestChats = signal<Omit<IChat<FromAPI>, 'messages'>[]>(undefined);

  readonly currentChat = signal<IChat<FromAPI>>(undefined);

  readonly currentChatStatus = signal<'none' | 'loading' | '404' | 'open'>('none');

  readonly generating = signal<boolean | string>(false);
  readonly streamOutputStarted = signal(false);
  readonly streamingAssistantMessage = signal<{ content: string } | null>(null);

  readonly reasoningEffort = signal<ChatReasoningEffort>(this.getInitialReasoningEffort());

  readonly reasoningEffortOptions: { value: ChatReasoningEffort; label: string }[] = [
    { value: 'low', label: 'Fastest' },
    { value: 'medium', label: 'balanced' },
    { value: 'high', label: 'deep' },
  ];

  readonly chatCapabilities = signal<IChatCapabilities>(undefined);

  readonly chatEnabled = computed(() => !!this.chatCapabilities()?.enabled);

  constructor() {
    this.init();
  }

  protected async init() {
    const [latestChats] = await Promise.all([
      lastValueFrom(
        this.httpClient.get<Omit<IChat<FromAPI>, 'messages'>[]>(
          environment.dataServiceApi + 'chats',
        ),
      ),
      this.loadCapabilities(),
    ]);

    this.latestChats.set(latestChats.map((chat) => this.withFriendlySummary(chat)));
  }

  async loadCapabilities() {
    try {
      this.chatCapabilities.set(
        await lastValueFrom(
          this.httpClient.get<IChatCapabilities>(`${environment.dataServiceApi}chat/capabilities`),
        ),
      );
    } catch {
      this.chatCapabilities.set({ enabled: false, dataSources: [] });
    }
  }

  reset() {
    this.responseStreamSubscription?.unsubscribe();
    this.responseStreamSubscription = undefined;
    this.generating.set(false);
    this.streamOutputStarted.set(false);
    this.streamingAssistantMessage.set(null);
    this.currentChat.set(undefined);
    this.currentChatStatus.set('none');
  }

  setReasoningEffort(value: ChatReasoningEffort) {
    this.reasoningEffort.set(value);
    localStorage.setItem(reasoningEffortStorageKey, value);
  }

  async loadChat(id: string) {
    this.reset();
    try {
      this.currentChatStatus.set('loading');
      this.currentChat.set(
        this.withFriendlySummary(
          (await lastValueFrom(
            this.httpClient.get<IChat<FromAPI>>(`${environment.dataServiceApi}chat/${id}`),
          )) ?? null,
        ),
      );
      this.currentChatStatus.set('open');
    } catch {
      this.currentChatStatus.set('404');
    }
  }

  async deleteChat(id: string) {
    await lastValueFrom(this.httpClient.delete(`${environment.dataServiceApi}chat/${id}`));
  }

  async renameChat(id: string, summary: string) {
    await lastValueFrom(
      this.httpClient.patch(`${environment.dataServiceApi}chat/${id}`, { summary }),
    );
  }

  async addMessage(message: string, textChunkCallback?: () => unknown) {
    if (!this.chatEnabled()) {
      return;
    }

    let url = `${environment.dataServiceApi}chat`;

    const currentChat = this.currentChat();

    if (currentChat) {
      url += `/${currentChat._id}`;
    }

    const messageObj = { role: 'user' as const, content: message };

    const chat = await lastValueFrom(
      this.httpClient.post<IChat<FromAPI>>(url, { messages: [messageObj] }),
    );

    if (currentChat) {
      this.currentChat().messages.push(messageObj);
      this.generateResponse(textChunkCallback);
      return this.currentChat()._id;
    } else {
      this.latestChats().unshift(this.withFriendlySummary(chat));
      return chat._id;
    }
  }

  generateSummary() {
    const chat = this.currentChat();

    if (!chat || chat.summary || summaryIsInProgress(chat)) {
      return;
    }

    this.httpClient
      .post(`${environment.dataServiceApi}chat/${chat._id}/summary`, null, {
        observe: 'events',
        responseType: 'text',
        reportProgress: true,
      })
      .subscribe((event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          chat.summary = (event as HttpDownloadProgressEvent).partialText;
          const friendlySummary = this.formatUserFacingLabel(chat.summary);
          chat.summary = friendlySummary;
          this.updateLatestChatSummary(chat._id, friendlySummary);
        } else if (event.type === HttpEventType.Response) {
          chat.summary = this.formatUserFacingLabel(event.body || chat.summary);
          this.updateLatestChatSummary(chat._id, chat.summary);
        }
      });
  }

  async generateResponse(onFinish?: () => void) {
    if (!this.chatEnabled()) {
      return;
    }

    const chat = this.currentChat();

    if (
      this.generating() ||
      !chat ||
      !latestMessageRoleIs(chat, 'user') ||
      responseIsInProgress(chat)
    ) {
      return;
    }

    this.generating.set(initialGeneratingText);
    this.streamOutputStarted.set(false);
    this.streamingAssistantMessage.set({ content: '' });

    let buffer = '';
    let index = 0;

    const processNdjsonChunk = (chunk: string) => {
      if (!chunk.length) {
        return;
      }

      buffer += chunk;

      if (!buffer.includes('\n')) {
        return;
      }

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.trim()) {
          continue;
        }

        let streamEvent: IFrontendAgentStreamEvent;

        try {
          streamEvent = JSON.parse(line) as IFrontendAgentStreamEvent;
        } catch {
          continue;
        }

        switch (streamEvent.type) {
          case 'output': {
            this.streamOutputStarted.set(true);
            this.generating.set(true);
            const current = this.streamingAssistantMessage();
            if (current) {
              this.streamingAssistantMessage.set({
                ...current,
                content: current.content + streamEvent.delta,
              });
            }
            break;
          }

          case 'reasoning': {
            const text = streamEvent.text.match(/\*\*(.+)\*\*/)?.[1]?.trim();
            if (text) {
              this.generating.set(text);
            }
            break;
          }

          case 'tool_call': {
            if (this.generating() === initialGeneratingText) {
              this.generating.set(this.formatToolCallStatus(streamEvent.name));
            }
            break;
          }

          case 'tile_instance': {
            // Embedded tile rendering has been removed from current chat.
            break;
          }
        }
      }
    };

    this.responseStreamSubscription?.unsubscribe();
    this.responseStreamSubscription = this.httpClient
      .post(
        `${environment.dataServiceApi}chat/${chat._id}/response`,
        { reasoningEffort: this.reasoningEffort() },
        {
          observe: 'events',
          responseType: 'text',
          reportProgress: true,
        },
      )
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const { partialText } = event as HttpDownloadProgressEvent;
            if (!partialText?.length) {
              return;
            }
            processNdjsonChunk(partialText.slice(index));
            index = partialText.length;
          } else if (event.type === HttpEventType.Response) {
            processNdjsonChunk((event.body ?? '').slice(index));
            processNdjsonChunk('\n');

            const streamingMsg = this.streamingAssistantMessage();
            if (streamingMsg?.content) {
              chat.messages.push({ role: 'assistant', ...streamingMsg });
            }
            this.generating.set(false);
            this.streamOutputStarted.set(false);
            this.streamingAssistantMessage.set(null);
            this.responseStreamSubscription = undefined;
            onFinish?.();
          }
        },
        error: () => {
          this.generating.set(false);
          this.streamOutputStarted.set(false);
          this.streamingAssistantMessage.set(null);
          this.responseStreamSubscription = undefined;
          void this.loadCapabilities();
        },
      });
  }

  private updateLatestChatSummary(chatId: string, summary: string) {
    const chats = this.latestChats();
    if (!chats?.length) {
      return;
    }

    const chatIndex = chats.findIndex((entry) => entry._id === chatId);

    if (chatIndex === -1) {
      return;
    }

    chats[chatIndex].summary = this.formatUserFacingLabel(summary);
  }

  private withFriendlySummary<T extends IChat<FromAPI> | Omit<IChat<FromAPI>, 'messages'>>(
    chat: T,
  ): T {
    if (!chat?.summary) {
      return chat;
    }

    return {
      ...chat,
      summary: this.formatUserFacingLabel(chat.summary),
    };
  }

  private formatToolCallStatus(toolName: string): string {
    if (toolName.startsWith('find_and_paginate_')) {
      return `Searching ${this.formatUserFacingLabel(toolName.slice(18)).toLowerCase()}`;
    }

    if (toolName.startsWith('aggregate_')) {
      return `Analyzing ${this.formatUserFacingLabel(toolName.slice(10)).toLowerCase()}`;
    }

    if (toolName.startsWith('find_one_by_id_')) {
      return `Checking ${this.formatUserFacingLabel(toolName.slice(15)).toLowerCase()} details`;
    }

    return this.formatUserFacingLabel(toolName);
  }

  formatUserFacingLabel(value?: string): string {
    const label = String(value ?? '').trim();

    if (!label) {
      return '';
    }

    if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(label)) {
      return label
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    return label;
  }
}
